import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import * as moment from 'moment';

/** The class a calendar puts on the OTHER end of the range, so the open picker shows where it stops. */
export const DATE_RANGE_ENDPOINT_CLASS = 'g15-date-range-endpoint';

/** A picked value: the app's DateAdapter answers a Date, a moment-based one a Moment. */
type DayValue = Date | moment.Moment;

/**
 * Reusable From/To date filter: two readonly datepickers that constrain each other.
 * Two-way bind both ends — `[(start)]` / `[(end)]` — and the labels as i18n keys.
 * The start calendar refuses days after `end` and marks the end day; the end calendar mirrors it.
 *
 * Example: `<app-date-range-filter startLabel="common.startDate" endLabel="common.endDate"
 *   [(start)]="from" [(end)]="to"></app-date-range-filter>`
 */
@Component({
  selector: 'app-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.scss'],
})
export class DateRangeFilterComponent {
  /** i18n key of the start field's label. */
  @Input() startLabel = '';
  /** i18n key of the end field's label. */
  @Input() endLabel = '';
  /** False for a range that must always have both ends: no clear buttons at all. */
  @Input() clearable = true;
  @Input() start: Date | null = null;
  @Output() startChange = new EventEmitter<Date | null>();
  @Input() end: Date | null = null;
  @Output() endChange = new EventEmitter<Date | null>();

  /** Each field's datepicker toggle: where focus goes when its clear button removes itself. */
  @ViewChild('startToggle', { read: ElementRef }) private startToggle?: ElementRef<HTMLElement>;
  @ViewChild('endToggle', { read: ElementRef }) private endToggle?: ElementRef<HTMLElement>;

  /** The start calendar allows only days up to the end, when one is set. */
  startFilter = (d: Date | null): boolean =>
    !d || !this.end || moment(d).isSameOrBefore(moment(this.end), 'day');

  /** The end calendar allows only days from the start, when one is set. */
  endFilter = (d: Date | null): boolean =>
    !d || !this.start || moment(d).isSameOrAfter(moment(this.start), 'day');

  /** The start calendar marks the end day (month view only). */
  startDateClass: MatCalendarCellClassFunction<Date> = (cell, view) => endpointClass(cell, view, this.end);

  /** The end calendar marks the start day (month view only). */
  endDateClass: MatCalendarCellClassFunction<Date> = (cell, view) => endpointClass(cell, view, this.start);

  onStart(value: DayValue | null): void {
    this.start = toDate(value);
    this.startChange.emit(this.start);
  }

  onEnd(value: DayValue | null): void {
    this.end = toDate(value);
    this.endChange.emit(this.end);
  }

  clear(event: Event | undefined, which: 'start' | 'end'): void {
    // Stopped so the click does not reach the form field's container-click handler, which focuses
    // the date input: a keyboard user who pressed the clear would lose it from under them.
    event?.stopPropagation();
    if ((which === 'start' ? this.start : this.end) == null) {
      return; // already empty: nothing to tell the host, and no search to trigger
    }
    // The clear button renders only while its field holds a date, so clearing destroys the very
    // button that has focus and focus would fall to <body> (WCAG 2.4.3). Move it to the same
    // field's calendar toggle first, while the button still exists.
    const toggle = (which === 'start' ? this.startToggle : this.endToggle)?.nativeElement;
    toggle?.querySelector<HTMLElement>('button')?.focus();
    if (which === 'start') {
      this.onStart(null);
    } else {
      this.onEnd(null);
    }
  }
}

function toDate(value: DayValue | null): Date | null {
  return value ? moment(value).toDate() : null;
}

/** Same calendar day, year included. */
function endpointClass(cell: DayValue, view: string, other: Date | null): string {
  return view === 'month' && other && moment(cell).isSame(moment(other), 'day') ? DATE_RANGE_ENDPOINT_CLASS : '';
}
